/*
 * Copyright 2021 Adobe. All rights reserved.
 * This file is licensed to you under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License. You may obtain a copy
 * of the License at http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 * OF ANY KIND, either express or implied. See the License for the specific language
 * governing permissions and limitations under the License.
 */
"use strict";

function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) { try { var info = gen[key](arg); var value = info.value; } catch (error) { reject(error); return; } if (info.done) { resolve(value); } else { Promise.resolve(value).then(_next, _throw); } }

function _asyncToGenerator(fn) { return function () { var self = this, args = arguments; return new Promise(function (resolve, reject) { var gen = fn.apply(self, args); function _next(value) { asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value); } function _throw(err) { asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err); } _next(undefined); }); }; }

function _wrapAsyncGenerator(fn) { return function () { return new _AsyncGenerator(fn.apply(this, arguments)); }; }

function _AsyncGenerator(gen) { var front, back; function send(key, arg) { return new Promise(function (resolve, reject) { var request = { key: key, arg: arg, resolve: resolve, reject: reject, next: null }; if (back) { back = back.next = request; } else { front = back = request; resume(key, arg); } }); } function resume(key, arg) { try { var result = gen[key](arg); var value = result.value; var wrappedAwait = value instanceof _AwaitValue; Promise.resolve(wrappedAwait ? value.wrapped : value).then(function (arg) { if (wrappedAwait) { resume(key === "return" ? "return" : "next", arg); return; } settle(result.done ? "return" : "normal", arg); }, function (err) { resume("throw", err); }); } catch (err) { settle("throw", err); } } function settle(type, value) { switch (type) { case "return": front.resolve({ value: value, done: true }); break; case "throw": front.reject(value); break; default: front.resolve({ value: value, done: false }); break; } front = front.next; if (front) { resume(front.key, front.arg); } else { back = null; } } this._invoke = send; if (typeof gen.return !== "function") { this.return = undefined; } }

_AsyncGenerator.prototype[typeof Symbol === "function" && Symbol.asyncIterator || "@@asyncIterator"] = function () { return this; };

_AsyncGenerator.prototype.next = function (arg) { return this._invoke("next", arg); };

_AsyncGenerator.prototype.throw = function (arg) { return this._invoke("throw", arg); };

_AsyncGenerator.prototype.return = function (arg) { return this._invoke("return", arg); };

function _awaitAsyncGenerator(value) { return new _AwaitValue(value); }

function _AwaitValue(value) { this.wrapped = value; }

function _asyncIterator(iterable) { var method; if (typeof Symbol !== "undefined") { if (Symbol.asyncIterator) method = iterable[Symbol.asyncIterator]; if (method == null && Symbol.iterator) method = iterable[Symbol.iterator]; } if (method == null) method = iterable["@@asyncIterator"]; if (method == null) method = iterable["@@iterator"]; if (method == null) throw new TypeError("Object is not async iterable"); return method.call(iterable); }

function _asyncGeneratorDelegate(inner, awaitWrap) { var iter = {}, waiting = false; function pump(key, value) { waiting = true; value = new Promise(function (resolve) { resolve(inner[key](value)); }); return { done: false, value: awaitWrap(value) }; } ; iter[typeof Symbol !== "undefined" && Symbol.iterator || "@@iterator"] = function () { return this; }; iter.next = function (value) { if (waiting) { waiting = false; return value; } return pump("next", value); }; if (typeof inner.throw === "function") { iter.throw = function (value) { if (waiting) { waiting = false; throw value; } return pump("throw", value); }; } if (typeof inner.return === "function") { iter.return = function (value) { if (waiting) { waiting = false; return value; } return pump("return", value); }; } return iter; }

const {
  AsyncGeneratorFunction
} = require("./function");
/**
 * Pipeline of functions are invoked sequentially
 * 
 * new PipelineFunction(func1, func2).execute(input) is equivalent to:
 * func2.execute(func1.execute(input))
 */


class Pipeline extends AsyncGeneratorFunction {
  /**
   * Compose a pipeline of functions that are invoked in-order
   * 
   * @param {...AsyncGeneratorFunction} funcs List of async generator functions
   */
  constructor(...funcs) {
    super();
    this.funcs = funcs;
  }
  /**
   * Configure a filter function
   * 
   * The filter function is executed called on every item before every pipeline step,
   * this allows items to be filtered out or adjusted.
   * 
   * @param {AsyncGeneratorFunction} filterFunc Filter function 
   */


  setFilterFunction(filterFunc) {
    this.filterFunc = filterFunc;
  }
  /**
   * Execute the pipeline of functions
   * 
   * @generator
   * @param {Object[]|Generator||AsyncGenerator} items Items to process
   * @param {...Object} args Arguments to pass to the functions
   * @yields {Object} 
   */


  execute(items, ...args) {
    var _this = this;

    return _wrapAsyncGenerator(function* () {
      let generator = items;

      for (const func of _this.funcs) {
        if (_this.filterFunc) {
          generator = _this.filterFunc.execute(generator, ...args);
        }

        generator = func.execute(generator, ...args);
      }

      yield* _asyncGeneratorDelegate(_asyncIterator(generator), _awaitAsyncGenerator);
    })();
  }

}
/**
 * Execute pipeline
 * 
 * @param {Pipeline} pipeline Pipeline to execute
 * @param {Object[]|Generator|AsyncGenerator} input Input items
 * @param {Object..} [args] Arguments to pass to the pipeline
 */


function executePipeline(_x, _x2) {
  return _executePipeline.apply(this, arguments);
}

function _executePipeline() {
  _executePipeline = _asyncToGenerator(function* (pipeline, input, ...args) {
    var _iteratorNormalCompletion = true;
    var _didIteratorError = false;

    var _iteratorError;

    try {
      for (var _iterator = _asyncIterator(pipeline.execute(input, ...args)), _step, _value; _step = yield _iterator.next(), _iteratorNormalCompletion = _step.done, _value = yield _step.value, !_iteratorNormalCompletion; _iteratorNormalCompletion = true) {
        const item = _value;
        item;
      }
    } catch (err) {
      _didIteratorError = true;
      _iteratorError = err;
    } finally {
      try {
        if (!_iteratorNormalCompletion && _iterator.return != null) {
          yield _iterator.return();
        }
      } finally {
        if (_didIteratorError) {
          throw _iteratorError;
        }
      }
    }
  });
  return _executePipeline.apply(this, arguments);
}

module.exports = {
  Pipeline,
  executePipeline
};